import { listQuartzOccurrences } from '@app/pages/admin/orchestration-quartz-cron.util';
import { ProcessGroupId, PROCESS_GROUPS, processGroupId } from '@app/pages/admin/orchestration-process-groups.util';

export const SCHEDULE_GANTT_WINDOWS = [24, 168] as const;
export type ScheduleGanttWindowHours = (typeof SCHEDULE_GANTT_WINDOWS)[number];

const MINUTE_MS = 60_000;
const DENSE_GAP_MS = 15 * MINUTE_MS;
const MIN_MARK_PERCENT = 0.45;

export interface ScheduleGanttInput {
  id: string;
  name: string;
  processKey: string;
  processLabel: string;
  cronExpression: string;
  timeZoneId: string;
  isActive: boolean;
  nextFireAt?: string | Date | null;
  detailsUrl: string | null;
}

export interface ScheduleGanttMark {
  atMs: number;
  endMs: number;
  leftPercent: number;
  widthPercent: number;
  overlapping: boolean;
  dense: boolean;
}

export interface ScheduleGanttAxisTick {
  atMs: number;
  leftPercent: number;
}

export interface ScheduleGanttRow {
  id: string;
  name: string;
  processLabel: string;
  groupId: ProcessGroupId;
  detailsUrl: string | null;
  isActive: boolean;
  usesNextFireOnly: boolean;
  marks: ScheduleGanttMark[];
}

export interface ScheduleGanttView {
  fromMs: number;
  toMs: number;
  windowHours: ScheduleGanttWindowHours;
  nowLeftPercent: number | null;
  axisTicks: ScheduleGanttAxisTick[];
  rows: ScheduleGanttRow[];
  overlapCount: number;
}

export function buildScheduleGanttView(
  items: ScheduleGanttInput[],
  windowHours: ScheduleGanttWindowHours,
  now: Date,
): ScheduleGanttView {
  const hours = windowHours === 168 ? 168 : 24;
  const fromMs = now.getTime();
  const toMs = fromMs + hours * 60 * MINUTE_MS;
  const spanMs = toMs - fromMs;
  const from = new Date(fromMs);
  const to = new Date(toMs);

  const rows = items.map(item => toGanttRow(item, from, to, fromMs, spanMs));
  const overlapMinutes = collectOverlapMinutes(rows);
  const overlapCount = overlapMinutes.size;
  for (const row of rows) {
    if (!row.isActive) {
      continue;
    }
    for (const mark of row.marks) {
      mark.overlapping = markOverlaps(mark, overlapMinutes);
    }
  }

  rows.sort((left, right) => {
    const groupDiff = groupRank(left.groupId) - groupRank(right.groupId);
    if (groupDiff !== 0) {
      return groupDiff;
    }
    const leftStart = left.marks[0]?.atMs ?? Number.POSITIVE_INFINITY;
    const rightStart = right.marks[0]?.atMs ?? Number.POSITIVE_INFINITY;
    if (leftStart !== rightStart) {
      return leftStart - rightStart;
    }
    return left.name.localeCompare(right.name);
  });

  return {
    fromMs,
    toMs,
    windowHours: hours,
    nowLeftPercent: null,
    axisTicks: buildAxisTicks(fromMs, toMs, hours, spanMs),
    rows,
    overlapCount,
  };
}

function toGanttRow(
  item: ScheduleGanttInput,
  from: Date,
  to: Date,
  fromMs: number,
  spanMs: number,
): ScheduleGanttRow {
  let fires = listQuartzOccurrences(
    item.cronExpression ?? '',
    item.timeZoneId ?? 'UTC',
    from,
    to,
  );
  const usesNextFireOnly = fires.length === 0;
  if (usesNextFireOnly) {
    const next = parseInstant(item.nextFireAt);
    if (next && next.getTime() >= from.getTime() && next.getTime() < to.getTime()) {
      fires = [next];
    }
  }

  return {
    id: item.id,
    name: item.name,
    processLabel: item.processLabel,
    groupId: processGroupId(item.processKey ?? ''),
    detailsUrl: item.detailsUrl,
    isActive: item.isActive,
    usesNextFireOnly,
    marks: toMarks(fires, fromMs, spanMs),
  };
}

function toMarks(fires: Date[], fromMs: number, spanMs: number): ScheduleGanttMark[] {
  if (!fires.length) {
    return [];
  }
  const times = fires.map(item => item.getTime()).sort((left, right) => left - right);
  const dense = isDense(times);
  if (dense) {
    const atMs = times[0];
    const endMs = Math.min(times[times.length - 1] + MINUTE_MS, fromMs + spanMs);
    return [toMark(atMs, endMs, fromMs, spanMs, true)];
  }
  return times.map(atMs => toMark(atMs, atMs + MINUTE_MS, fromMs, spanMs, false));
}

function isDense(times: number[]): boolean {
  if (times.length >= 96) {
    return true;
  }
  if (times.length < 8) {
    return false;
  }
  const gap = times[1] - times[0];
  return gap > 0 && gap <= DENSE_GAP_MS;
}

function toMark(
  atMs: number,
  endMs: number,
  fromMs: number,
  spanMs: number,
  dense: boolean,
): ScheduleGanttMark {
  const leftPercent = percent(atMs - fromMs, spanMs);
  const rawWidth = percent(Math.max(endMs - atMs, MINUTE_MS), spanMs);
  return {
    atMs,
    endMs,
    leftPercent,
    widthPercent: Math.min(100 - leftPercent, Math.max(MIN_MARK_PERCENT, rawWidth)),
    overlapping: false,
    dense,
  };
}

function percent(part: number, whole: number): number {
  if (whole <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(100, (part / whole) * 100));
}

function collectOverlapMinutes(rows: ScheduleGanttRow[]): Set<number> {
  const counts = new Map<number, number>();
  for (const row of rows) {
    if (!row.isActive) {
      continue;
    }
    for (const minute of markMinutes(row.marks)) {
      counts.set(minute, (counts.get(minute) ?? 0) + 1);
    }
  }
  const overlapping = new Set<number>();
  for (const [minute, count] of counts) {
    if (count >= 2) {
      overlapping.add(minute);
    }
  }
  return overlapping;
}

function markMinutes(marks: ScheduleGanttMark[]): number[] {
  const minutes: number[] = [];
  for (const mark of marks) {
    if (mark.dense) {
      const start = Math.floor(mark.atMs / MINUTE_MS);
      const end = Math.floor((mark.endMs - 1) / MINUTE_MS);
      for (let minute = start; minute <= end; minute += 1) {
        minutes.push(minute);
      }
      continue;
    }
    minutes.push(Math.floor(mark.atMs / MINUTE_MS));
  }
  return minutes;
}

function markOverlaps(mark: ScheduleGanttMark, overlapMinutes: Set<number>): boolean {
  if (mark.dense) {
    const start = Math.floor(mark.atMs / MINUTE_MS);
    const end = Math.floor((mark.endMs - 1) / MINUTE_MS);
    for (let minute = start; minute <= end; minute += 1) {
      if (overlapMinutes.has(minute)) {
        return true;
      }
    }
    return false;
  }
  return overlapMinutes.has(Math.floor(mark.atMs / MINUTE_MS));
}

function buildAxisTicks(
  fromMs: number,
  toMs: number,
  hours: ScheduleGanttWindowHours,
  spanMs: number,
): ScheduleGanttAxisTick[] {
  const stepHours = hours === 168 ? 24 : 3;
  const ticks: ScheduleGanttAxisTick[] = [];
  for (let hour = 0; hour <= hours; hour += stepHours) {
    const atMs = fromMs + hour * 60 * MINUTE_MS;
    if (atMs > toMs) {
      break;
    }
    ticks.push({ atMs, leftPercent: percent(atMs - fromMs, spanMs) });
  }
  return ticks;
}

function groupRank(groupId: ProcessGroupId): number {
  const index = PROCESS_GROUPS.findIndex(group => group.id === groupId);
  return index === -1 ? PROCESS_GROUPS.length : index;
}

function parseInstant(value: string | Date | null | undefined): Date | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
